import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import T from '../../../translate';
import { useTimer } from '../../hooks';
import { ArrowRight } from '../../icons';
import { DEFAULT_COUNTDOWN_TIMER } from '../../utils/constants';

type Props = {
  /** duration in millisecond */
  duration: number;
  /** auto start timer on component mount */
  startOnComponentMount?: boolean;
  /** Callback when the timer hits 0 */
  onTimeout?(): void;
};

type Methods = {
  startTimer(): void;
};

const CountdownTimer = forwardRef<Methods, Props>(
  ({ duration = DEFAULT_COUNTDOWN_TIMER, startOnComponentMount = true, onTimeout }: Props, ref) => {
    const { timer, isRunning, isFinished, startTimer, clearTimer } = useTimer((tick) => {
      if (tick.done) {
        onTimeout?.();
      }
    });

    const countdown = useMemo(() => {
      const _countdown = isRunning || isFinished ? timer : duration;
      return _countdown / 1000;
    }, [timer, isRunning, isFinished, duration]);

    useEffect(() => {
      if (startOnComponentMount) {
        startTimer({ duration });
      }
      return clearTimer;
      /** Only want to execute once on mount and unmount */
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useImperativeHandle(ref, () => ({
      startTimer() {
        startTimer({ duration });
      },
    }));

    return (
      <TimerWrapper>
        <Progress duration={duration} animate={isRunning || isFinished} />
        <Text>{T.VideoWillPlayIn(countdown)}</Text> <ArrowRight />
      </TimerWrapper>
    );
  },
);

const FillAnimation = keyframes`
    to {
        width: 100%;
    } 
`;

const Text = styled.div`
  margin-right: 6px;
`;

const Progress = styled.div<{ duration: number; animate: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 0%;
  height: 100%;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.1);
  ${({ duration, animate }) =>
    animate &&
    css`
      animation: ${FillAnimation} ${duration}ms linear;
      animation-fill-mode: forwards;
    `}
`;

const TimerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 6px 10px 6px 12px;
  gap: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  position: relative;
  overflow: hidden;
`;

export default CountdownTimer;
